import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { CashReceiptModel } from 'src/app/models/wallet/cash-receipt.model';
import { InvoiceCashReceiptModel } from 'src/app/models/wallet/invoice-cash-receipt.model';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { InvoiceCashReceiptService } from 'src/app/services/wallet/invoice-cash-receipt.service';

@Component({
  selector: 'app-register-invoice-cash-receipt',
  templateUrl: './register-invoice-cash-receipt.component.html',
  styleUrls: ['./register-invoice-cash-receipt.component.scss'],
  standalone: false
})
export class RegisterInvoiceCashReceiptComponent implements OnChanges {

  @Input() cashReceiptModel: CashReceiptModel = new CashReceiptModel();
  @Input() invoiceCashReceiptContract: InvoiceCashReceiptModel = new InvoiceCashReceiptModel();
  @Input() permissionUpdatable: boolean = false;
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  formInvoiceCashReceipt: FormGroup = new FormGroup({});
  invoiceCashReceiptId: number = 0;
  oldDiscount: number = 0;
  residueValueInMemory: number = 0;
  subscription: Subscription;

  constructor(private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private invoiceCashReceiptService: InvoiceCashReceiptService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.invoiceCashReceiptContract);
  }

  async ngOnChanges(changes: SimpleChanges) {
    const invoiceCashReceiptContract = changes["invoiceCashReceiptContract"];
    if (invoiceCashReceiptContract && this.invoiceCashReceiptContract.invoiceCashReceiptId === 0) {
      this.createNewInvoiceCashReceipt();
    } else {
      this.invoiceCashReceiptId = this.invoiceCashReceiptContract.invoiceCashReceiptId;
      this.validateForm(this.invoiceCashReceiptContract);
    }
  }

  changeDiscountValue(newDiscount: number) {
    const paid = this.formInvoiceCashReceipt.controls["paidValue"].value;

    if (newDiscount === 0 && this.oldDiscount !== 0) {
      const newPaid = paid + this.oldDiscount;
      this.formInvoiceCashReceipt.controls["paidValue"].patchValue(newPaid, { emitEvent: false });
    } else if (newDiscount !== 0 && newDiscount < this.oldDiscount) {
      const newPaid = paid + (this.oldDiscount - newDiscount);
      this.formInvoiceCashReceipt.controls["paidValue"].patchValue(newPaid, { emitEvent: false });
    } else if (newDiscount !== 0 && newDiscount > this.oldDiscount) {
      const newPaid = paid - (newDiscount - this.oldDiscount);
      this.formInvoiceCashReceipt.controls["paidValue"].patchValue(newPaid, { emitEvent: false });
    }

    this.oldDiscount = newDiscount;
    this.calculateValues();
  }

  calculateValues() {
    const discount = this.formInvoiceCashReceipt.controls["discountValue"].value;
    let paid = this.formInvoiceCashReceipt.controls["paidValue"].value;

    let residueBase = this.invoiceCashReceiptContract.residueValueInvoice;
    const totalDeductions = (this.invoiceCashReceiptContract.reteIcaValue) + (this.invoiceCashReceiptContract.reteIvaValue) +
      (this.invoiceCashReceiptContract.reteFuenteValue) + (this.invoiceCashReceiptContract.otherTaxes);
    const paidValueContract = this.invoiceCashReceiptContract.paidValue + this.invoiceCashReceiptContract.discountValue + totalDeductions;
    if (residueBase === 0) {
      residueBase = paidValueContract;
    }
    if (residueBase !== paidValueContract) {
      residueBase = residueBase + paidValueContract;
    }
    const availableValue = residueBase - totalDeductions; // (saldo disponible)
    let typedValue = discount + paid; // Suma de valores ingresados

    if (typedValue > availableValue) {
      // Si nos pasamos se intenta compensar bajando el valor pagado primero
      const overflow = typedValue - availableValue;

      if (overflow <= paid) {
        // Quita el exceso del valor pagado
        paid = paid - overflow;
        this.formInvoiceCashReceipt.controls["paidValue"].patchValue(paid, { emitEvent: false });
        typedValue = discount + paid;
        this.residueValueInMemory = availableValue - typedValue;
      } else {
        this.residueValueInMemory = residueBase - totalDeductions;
        this.formInvoiceCashReceipt.controls["discountValue"].patchValue(0, { emitEvent: false });
        this.formInvoiceCashReceipt.controls["paidValue"].patchValue(0, { emitEvent: false });
        this.toastHandlerService.showToast(
          'error',
          'La suma del descuento y el valor pagado no puede superar el saldo del documento.'
        );
        return;
      }
    } else {
      this.residueValueInMemory = availableValue - typedValue;
    }
  }

  clearForm() {
    this.invoiceCashReceiptContract = new InvoiceCashReceiptModel();
    this.invoiceCashReceiptId = this.invoiceCashReceiptContract.invoiceCashReceiptId;
    this.isFormView.emit(false);
  }

  createNewInvoiceCashReceipt() {
    this.invoiceCashReceiptId = 0;
    this.validateForm(this.invoiceCashReceiptContract);
  }

  async send() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar recibos de caja. Comuníquese con el administrador del sistema.');
      return;
    }
    Object.keys(this.formInvoiceCashReceipt.controls).forEach(key => {
      const control = this.formInvoiceCashReceipt.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInvoiceCashReceipt.valid) {
      const modelInvoiceCashReceipt = this.formInvoiceCashReceipt.getRawValue() as InvoiceCashReceiptModel;

      if (this.invoiceCashReceiptId === 0) {
        try {
          await firstValueFrom(this.invoiceCashReceiptService.createAsync(modelInvoiceCashReceipt));
          this.toastHandlerService.showToast(
            'success',
            'El detalle del recibo de caja fue creado exitosamente.'
          );
          this.updateListDetail.emit(true);
          this.isFormView.emit(false);
        } catch (error) {
          console.error('Error al crear el detalle del recibo de caja.', error);
        }
      } else {
        this.invoiceCashReceiptContract.discountValue = modelInvoiceCashReceipt.discountValue;
        this.invoiceCashReceiptContract.paidValue = modelInvoiceCashReceipt.paidValue;
        try {
          await firstValueFrom(this.invoiceCashReceiptService.updateInvoiceCashReceipt(this.invoiceCashReceiptContract));
          this.toastHandlerService.showToast(
            'success',
            'El detalle del recibo de caja fue modificado exitosamente.'
          );
          this.updateListDetail.emit(true);
          this.isFormView.emit(false);
        } catch (error) {
          this.updateListDetail.emit(true);
          console.error('Error al modificar el detalle del recibo de caja.', error);
        }
      }
      this.createNewInvoiceCashReceipt();
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      discountValue: {
        required: 'El campo Descuento es obligatorio.',
        min: 'El Descuento debe ser mayor o igual a 0.'
      },
      paidValue: {
        required: 'El campo Vr.Pagado es obligatorio.',
        min: 'El Vr.Pagado debe ser mayor o igual a 0.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInvoiceCashReceipt.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormState() {
    this.formControlStateService.setControlState(this.formInvoiceCashReceipt, ['document'], false);
    this.formControlStateService.setControlState(this.formInvoiceCashReceipt, ['discountValue', 'paidValue'], true);
  }

  validateForm(invoiceCashReceipt: InvoiceCashReceiptModel) {
    this.formInvoiceCashReceipt = this.formBuilder.group({
      cashReceiptId: [invoiceCashReceipt.cashReceiptId, [Validators.required, Validators.min(1)]],
      invoiceId: [invoiceCashReceipt.invoiceId, [Validators.required, Validators.min(1)]],
      document: [`${this.cashReceiptModel.isChargeAccount ? 'C' : 'F'} - ${invoiceCashReceipt.consecutiveInvoice}`],
      residueValue: [invoiceCashReceipt.residueValueInvoice, [Validators.required, Validators.min(0)]],
      discountValue: [invoiceCashReceipt.discountValue, [Validators.required, Validators.min(0)]],
      paidValue: [invoiceCashReceipt.paidValue, [Validators.required, Validators.min(0)]],
    });
    this.calculateValues();
    this.updateFormState();
  }

}
